import React, { useState } from 'react';
import { 
  BookOpen, 
  Search, 
  Clock, 
  FileCheck2, 
  ExternalLink, 
  Sparkles, 
  ChevronRight,
  BookMarked,
  Layers,
  X
} from 'lucide-react';
import { KNOWLEDGE_LIBRARY, CANONICAL_REVIEW_NOTE } from '../constants';
import { KnowledgeItem } from '../types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ReviewNotice from './ReviewNotice';

export default function KnowledgeHub() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [readingItem, setReadingItem] = useState<KnowledgeItem | null>(null);

  const categories = [
    { id: 'all', label: 'All Knowledge' },
    { id: 'foundational', label: 'Foundational / Philosophy' },
    { id: 'quran', label: 'Qur\'an Reflections' },
    { id: 'seerah', label: 'Seerah Historical Studies' },
    { id: 'sme', label: 'SME & Economic Formalization' },
    { id: 'developmental', label: 'Emotional & Developmental' }
  ];

  const filteredItems = KNOWLEDGE_LIBRARY.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.authorOrSource.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div id="knowledge" className="py-16 px-6 bg-slate-950 text-white border-t border-slate-800">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-semibold uppercase tracking-wider mb-2">
              meuussoul.com · Learning & Reflection Home
            </div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white tracking-tight">
              Knowledge Hub & Source Library
            </h2>
            <p className="text-slate-400 text-sm md:text-base mt-1 max-w-2xl">
              "Knowledge Before Action, Listen Before Knowledge." Access foundational texts, Qur'an & Seerah developmental readings, and practical SME/legal guides with full source transparency.
            </p>
          </div>

          <div className="w-full md:w-72 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
            <input
              type="text"
              placeholder="Search knowledge by topic or source..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-2xl bg-slate-900 border border-slate-800 focus:border-blue-500 text-xs text-white placeholder:text-slate-500 outline-none"
            />
          </div>
        </div>

        {/* Global Banner Notice */}
        <div className="mb-8">
          <ReviewNotice 
            variant="banner"
            customContext="All knowledge materials are published for open learning, verification, and practical accessibility. Scholarly, legal, and professional peer reviews continue asynchronously."
          />
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Knowledge Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div 
              key={item.id}
              className="p-6 rounded-3xl bg-slate-900/60 border border-slate-800 hover:border-blue-500/50 transition-all duration-300 flex flex-col justify-between group shadow-lg"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Badge variant="outline" className="text-[10px] font-mono tracking-wider border-blue-500/30 text-blue-300">
                    {item.category.toUpperCase()}
                  </Badge>
                  <span className="text-[11px] font-mono text-slate-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {item.readTime}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors leading-snug">
                  {item.title}
                </h3>

                <p className="text-xs text-slate-400 leading-relaxed mb-4 line-clamp-3">
                  {item.summary}
                </p>

                <div className="p-3 rounded-2xl bg-slate-950/60 border border-slate-800/80 mb-4 space-y-1 text-[11px]">
                  <div className="text-slate-400">
                    <span className="font-semibold text-slate-300">Source:</span> {item.authorOrSource}
                  </div>
                  <div className="text-slate-400">
                    <span className="font-semibold text-slate-300">Evidence Level:</span> {item.evidenceLevel}
                  </div>
                </div>

                {item.reviewStatus === 'REVIEW_IN_PROGRESS' && (
                  <div className="mb-4">
                    <ReviewNotice variant="inline" />
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between">
                <span className="text-[11px] font-mono text-emerald-400">
                  {item.reviewStatus}
                </span>
                <Button
                  size="sm"
                  onClick={() => setReadingItem(item)}
                  className="rounded-xl bg-blue-600/80 hover:bg-blue-600 text-white font-semibold text-xs h-8 px-3.5"
                >
                  <span>Read Text</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Reading Modal */}
        {readingItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
            <div className="w-full max-w-3xl max-h-[85vh] bg-slate-900 border border-slate-700 rounded-3xl p-6 md:p-10 overflow-y-auto shadow-2xl space-y-6">
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-800">
                <div>
                  <Badge variant="outline" className="border-blue-500/30 text-blue-400 text-[10px] font-mono mb-2">
                    {readingItem.category.toUpperCase()} · {readingItem.readTime}
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-white">
                    {readingItem.title}
                  </h3>
                  <p className="text-xs text-slate-400 font-mono mt-1">
                    Source: {readingItem.authorOrSource} | Level: {readingItem.evidenceLevel}
                  </p>
                </div>
                <button
                  onClick={() => setReadingItem(null)}
                  className="p-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <ReviewNotice 
                variant="card"
                topic={readingItem.title}
                customContext={readingItem.reviewNote}
              />

              <div className="text-slate-200 text-sm md:text-base leading-relaxed whitespace-pre-line space-y-4 font-sans">
                {readingItem.content}
              </div>

              <div className="pt-6 border-t border-slate-800 flex justify-between items-center text-xs text-slate-500 font-mono">
                <span>meuussoul.com · Preserved in Canonical Library</span>
                <Button 
                  onClick={() => setReadingItem(null)}
                  className="bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs"
                >
                  Close Reader
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
